import { getValidatedAmountsDto } from '../../coins'
import { getPairPrice, TGetPairPriceApiProps, TPriceRespApiDto } from '../../coins'
import { ECoinTypeAvailabilityMode } from '../../coins'
import { IAvailablePairDto, ICurrencyDto, IValidatedPairsDto } from '../../coins'
import { TCoinWidgetState } from '../index'
import { getQueriedPairExchangeWidgetState, TQueryDto } from './getSelectedCoinsFromQuery'
import { mapPairPriceToWidgetStateState } from './mapPairPriceToWidgetStateState'
import { getClampUptoRangeAmount } from '../index'

const getPairPriceApiPropsFromAvailableCurrencyDto = (
    availablePairDto: IAvailablePairDto,
): TGetPairPriceApiProps => {
    const { name, minAmountFrom } = availablePairDto

    return <TGetPairPriceApiProps>{
        pair: name,
        amountFrom: minAmountFrom + minAmountFrom * 0.1,
    }
}

const getDefaultPairPriceProps = ({
    allAvailablePairs,
    coinTypeAvailabilityModeFrom,
    coinTypeAvailabilityModeTo,
}: {
    allAvailablePairs: IAvailablePairDto[]
    coinTypeAvailabilityModeFrom: ECoinTypeAvailabilityMode
    coinTypeAvailabilityModeTo: ECoinTypeAvailabilityMode
}): TGetPairPriceApiProps => {
    // EUR | USD -> BTC || 'BTCEUR' || 'BTCUSD'
    // BTC -> EUR | USD || 'EURBTC' || 'USDBTC'
    // TODO: better have MAP for valid pairs instead of LIST
    const validInitPairMap = allAvailablePairs.reduce((validInitPairMap, validPair) => {
        const { name } = validPair

        if (coinTypeAvailabilityModeTo === ECoinTypeAvailabilityMode.FIAT) {
            // crypto to fiat
            if (name === 'EURBTC' || name === 'USDBTC') {
                validInitPairMap.set(name, validPair)
            }
        } else {
            // Fiat to crypto || crypto to crypto
            if (name === 'ETHBTC' || name === 'BTCEUR' || name === 'BTCUSD') {
                validInitPairMap.set(name, validPair)
            }
        }
        return validInitPairMap
    }, new Map<string, IAvailablePairDto>())

    if (coinTypeAvailabilityModeTo === ECoinTypeAvailabilityMode.FIAT) {
        if (validInitPairMap.has('EURBTC')) {
            return getPairPriceApiPropsFromAvailableCurrencyDto(validInitPairMap.get('EURBTC'))
        }
        if (validInitPairMap.has('USDBTC')) {
            return getPairPriceApiPropsFromAvailableCurrencyDto(validInitPairMap.get('USDBTC'))
        }
    } else if (coinTypeAvailabilityModeFrom === ECoinTypeAvailabilityMode.FIAT) {
        if (validInitPairMap.has('BTCEUR')) {
            return getPairPriceApiPropsFromAvailableCurrencyDto(validInitPairMap.get('BTCEUR'))
        }
        if (validInitPairMap.has('BTCUSD')) {
            return getPairPriceApiPropsFromAvailableCurrencyDto(validInitPairMap.get('BTCUSD'))
        }
    } else {
        if (validInitPairMap.has('ETHBTC')) {
            return getPairPriceApiPropsFromAvailableCurrencyDto(validInitPairMap.get('ETHBTC'))
        }
    }
    return getPairPriceApiPropsFromAvailableCurrencyDto(allAvailablePairs[0])
}

const getInitSelectedCoinsState = async ({
    validatedPairsDto,
    availableCurrencyDtoList,
    coinTypeAvailabilityModeFrom,
    coinTypeAvailabilityModeTo,
}: {
    validatedPairsDto: IValidatedPairsDto
    availableCurrencyDtoList: ICurrencyDto[]
    coinTypeAvailabilityModeFrom: ECoinTypeAvailabilityMode
    coinTypeAvailabilityModeTo: ECoinTypeAvailabilityMode
}): Promise<TCoinWidgetState> => {
    const getPairPricePropsDto = getDefaultPairPriceProps({
        allAvailablePairs: validatedPairsDto.allAvailablePairs,
        coinTypeAvailabilityModeFrom,
        coinTypeAvailabilityModeTo,
    })

    const apiPairPrices = await getPairPrice(getPairPricePropsDto)
    const { wasAmountValid, ...initAmountsDto } = getValidatedAmountsDto(apiPairPrices)
    const selectedCoinPrices = <TPriceRespApiDto>{
        ...apiPairPrices,
        ...initAmountsDto,
    }

    return mapPairPriceToWidgetStateState(selectedCoinPrices, availableCurrencyDtoList)
}

const getInitExchangePair = async ({
    query,
    validatedPairsDto,
    availableCurrencyDtoList,
    coinTypeAvailabilityModeFrom,
    coinTypeAvailabilityModeTo,
}: {
    query: TQueryDto
    validatedPairsDto: IValidatedPairsDto
    availableCurrencyDtoList: ICurrencyDto[]
    coinTypeAvailabilityModeFrom: ECoinTypeAvailabilityMode
    coinTypeAvailabilityModeTo: ECoinTypeAvailabilityMode
}): Promise<{
    exchangeWidgetState: TCoinWidgetState
    queryError: string
}> => {
    const { exchangeWidgetState: queriedPairState, queryError } =
        await getQueriedPairExchangeWidgetState({
            query,
            validatedPairsDto,
            availableCurrencyDtoList,
            coinTypeAvailabilityModeFrom,
        })

    if (queriedPairState) {
        return { exchangeWidgetState: queriedPairState, queryError }
    } else {
        const exchangeWidgetState = await getInitSelectedCoinsState({
            validatedPairsDto,
            availableCurrencyDtoList,
            coinTypeAvailabilityModeFrom,
            coinTypeAvailabilityModeTo,
        })

        const { from, to } = exchangeWidgetState
        const roundedFromAmount = getClampUptoRangeAmount(from.amount, true)
        const roundedToAmount = getClampUptoRangeAmount(to.amount, true)

        const updatedFromDto = { ...from, amount: roundedFromAmount }
        const updatedToDto = { ...to, amount: roundedToAmount }

        const updatedExchangeWidgetState = {
            ...exchangeWidgetState,
            from: updatedFromDto,
            to: updatedToDto,
        }

        return { exchangeWidgetState: updatedExchangeWidgetState, queryError }
    }
}

export { getInitExchangePair, getQueriedPairExchangeWidgetState }
